import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Center,
  Divider,
  Grid,
  HStack,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  ConnectionMode,
  ConnectionLineType,
  Controls,
  MarkerType,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  getConnectedEdges,
  getIncomers,
  getOutgoers,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FcRedo, FcShare, FcUndo } from 'react-icons/fc';
import { shallow } from 'zustand/shallow';

import useUserStore from '../../Hooks/Zustand/Store';
import useFlowchartStore from '../../Hooks/Zustand/flowchartStore';
import useCopyPaste from '../../Components/Flowchart/useCopyPaste';
import UseUndoRedo from '../../Components/Flowchart/useUndoRedo';
import {
  arrayUnionFirebase,
  getDocWithSnapshotFirebase,
  setDocumentFirebase,
} from '../../Api/firebaseApi';
import { clientTypessense } from '../../Api/Typesense';
import { dataButton, dataColor } from '../../Components/Flowchart/dataButton';
import ProductivityShapeNode from './ProductivityShapeNodePage';
import BackButtons from '../../Components/Buttons/BackButtons';
import DownloadButtonComponent from '../../Components/Buttons/DownloadButtonComponent';
import ModalAddUser from '../Workflow/Components/ModalAddUser';

// ==================== CONSTANTS ====================
const PRO_OPTIONS = { account: 'paid-pro', hideAttribution: true };
const DEFAULT_EDGE_OPTIONS = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed },
  style: { strokeWidth: 2 },
};

const NODE_TYPES = {
  shape: ProductivityShapeNode,
};

// Node configuration
const NODE_CONFIG = {
  diamond: { shape: 'diamond', color: '#fdc606', width: 120, height: 120 },
  parallelogram: { shape: 'parallelogram', width: 150, height: 70, color: '#ffb884' },
  hexagon: { shape: 'hexagon', width: 150, height: 70, color: '#d9ead3' },
  circle: { shape: 'circle', width: 70, height: 70, color: '#ffffff' },
  'arrow-rect': { shape: 'arrow-rect', width: 130, height: 50, color: '#784be8' },
  database: { shape: 'database', width: 100, height: 80, color: '#9ca8b3' },
  triangle: { shape: 'triangle', width: 100, height: 70, color: '#ff6700' },
  process: { shape: 'process', width: 90, height: 90, color: '#f17924' },
  document: { shape: 'document', width: 120, height: 60, color: '#7030a1' },
  display: { shape: 'display', width: 90, height: 60, color: '#9ca8b3' },
  'manual-operation': { shape: 'manual-operation', width: 120, height: 60, color: '#9ca8b3' },
  'offpage-reference': { shape: 'offpage-reference', width: 100, height: 60, color: '#9ca8b3' },
  default: { shape: 'round-rect', width: 150, height: 75, color: '#9ca8b3' },
};

// ==================== MAIN COMPONENT ====================
function FlowchartCanvas() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const globalState = useUserStore();

  // Store
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    setNodes,
    setEdges,
    setTitle,
    owner,
    setOwner,
    title,
  } = useFlowchartStore(
    (state) => ({
      nodes: state.nodes,
      edges: state.edges,
      onNodesChange: state.onNodesChange,
      onEdgesChange: state.onEdgesChange,
      onConnect: state.onConnect,
      setNodes: state.setNodes,
      setEdges: state.setEdges,
      setTitle: state.setTitle,
      owner: state.owner,
      setOwner: state.setOwner,
      title: state.title,
    }),
    shallow
  );

  // Undo/Redo & Copy/Paste
  const { undo, redo, canUndo, canRedo, takeSnapshot } = UseUndoRedo();
  const { copy } = useCopyPaste();

  // Local state
  const [flowchart, setFlowchart] = useState(null);
  const [latestId, setLatestId] = useState(0);
  const [latestPos, setLatestPos] = useState({ x: 0, y: 0 });
  const [edgeId, setEdgeId] = useState(0);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedUserProjectIds, setSelectedUserProjectIds] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [access, setAccess] = useState('');
  const [modalProjectUser, setModalProjectUser] = useState(false);

  // ==================== FETCH FLOWCHART ====================
  const getFlowchart = useCallback(async () => {
    try {
      const res = await getDocWithSnapshotFirebase('flowcharts', id);
      if (res) {
        setTitle(res.title);
        setFlowchart(res);
        setNodes(res.nodes || []);
        setEdges(res.edges || []);
        setLatestId(res.nodes?.length || 0);
        setEdgeId(res.edges?.length || 0);
        
        if (res.nodes && res.nodes.length > 0) {
          const lastNode = res.nodes[res.nodes.length - 1];
          setLatestPos({
            x: lastNode.position.x,
            y: lastNode.position.y,
          });
        }

        setOwner(res.owner?.includes(globalState?.uid) || false);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [id, globalState?.uid, setNodes, setEdges, setTitle, setOwner, toast]);

  useEffect(() => {
    getFlowchart();
    return () => {
      setFlowchart(null);
      setEdges([]);
      setNodes([]);
      setAccess('');
      setSelectedUserProjectIds([]);
      setOwner(false);
      setSearchResult([]);
      setModalProjectUser(false);
    };
  }, [getFlowchart]);

  // ==================== NODE OPERATIONS ====================
  
  /**
   * Add new node
   */
  const handleAdd = useCallback(
    async (type) => {
      takeSnapshot();

      const config = NODE_CONFIG[type] || NODE_CONFIG.default;
      
      const newNode = {
        id: `${latestId + 1}`,
        type: 'shape',
        position: { x: latestPos.x, y: latestPos.y + 150 },
        data: config,
      };

      const newEdge = {
        id: `edge-${latestId}-${latestId + 1}`,
        source: `${latestId}`,
        target: `${latestId + 1}`,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'smoothstep',
      };

      try {
        await setDocumentFirebase('flowcharts', id, {
          nodes: [...nodes, newNode],
          edges: [...edges, newEdge],
          companyId: globalState?.currentCompany,
          projectId: globalState?.currentProject,
        });

        setNodes([...nodes, newNode]);
        setEdges([...edges, newEdge]);
        setLatestId(latestId + 1);
        setEdgeId(edgeId + 1);
        setLatestPos({ x: latestPos.x, y: latestPos.y + 150 });

        toast({
          title: 'Node added',
          status: 'success',
          duration: 1000,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000,
        });
      }
    },
    [nodes, edges, latestId, edgeId, latestPos, id, globalState, setNodes, setEdges, takeSnapshot, toast]
  );

  /**
   * Change node color
   */
  const handleColorChange = useCallback(
    (selectedColor) => {
      if (!selectedNode) return;

      const updatedNodes = nodes.map((node) =>
        node.id === selectedNode.id
          ? {
            ...node,
            data: { ...node.data, color: selectedColor },
          }
          : node
      );

      setNodes(updatedNodes);
    },
    [selectedNode, nodes, setNodes]
  );

  // ==================== EVENT HANDLERS ====================
  
  const onNodeClick = useCallback(
    (e, node) => {
      setSelectedNode(node);
      copy();
    },
    [copy]
  );

  const onNodeDrag = useCallback((e, node) => {
    setLatestPos({ x: node.position.x, y: node.position.y });
  }, []);

  const onNodeDragStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onSelectionDragStart = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  const onNodesDelete = useCallback(
    (deleted) => {
      takeSnapshot();

      setEdges(
        deleted.reduce((acc, node) => {
          const incomers = getIncomers(node, nodes, edges);
          const outgoers = getOutgoers(node, nodes, edges);
          const connectedEdges = getConnectedEdges([node], edges);

          const remainingEdges = acc.filter(
            (edge) => !connectedEdges.includes(edge)
          );

          const createdEdges = incomers.flatMap(({ id: source }) =>
            outgoers.map(({ id: target }) => ({
              id: `edge-${source}-${target}`,
              source,
              target,
              type: 'smoothstep',
            }))
          );

          return [...remainingEdges, ...createdEdges];
        }, edges)
      );
    },
    [nodes, edges, setEdges, takeSnapshot]
  );

  const onEdgesDelete = useCallback(() => {
    takeSnapshot();
  }, [takeSnapshot]);

  /**
   * Save flowchart
   */
  const handleSave = useCallback(async () => {
    try {
      const newData = {
        ...flowchart,
        edges,
        nodes,
        lastUpdated: new Date(),
        lastUpdatedBy: {
          uid: globalState.uid,
          email: globalState.email,
        },
        companyId: globalState.currentCompany,
        projectId: globalState.currentProject,
      };

      const res = await setDocumentFirebase('flowcharts', id, newData);

      toast({
        title: 'Saved',
        description: res.message,
        status: 'success',
        duration: 2000,
      });

      navigate('/flowchart');
    } catch (error) {
      toast({
        title: t('toast.error'),
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [flowchart, edges, nodes, id, globalState, navigate, toast, t]);

  /**
   * Search users
   */
  const handleSearchUsers = useCallback(
    (query) => {
      const companyUsers = globalState.companies.find(
        (x) => x.id === globalState.currentCompany
      );

      if (!companyUsers?.users) return;

      const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      const userChunks = chunkArray(companyUsers.users, 100);

      const searchPromises = userChunks.map((userChunk) => {
        const searchParameters = {
          q: query,
          query_by: 'name,email',
          filter_by: `id: [${userChunk.join(',')}]`,
          sort_by: '_text_match:desc',
        };

        return clientTypessense
          .collections('users')
          .documents()
          .search(searchParameters);
      });

      Promise.all(searchPromises)
        .then((results) => {
          const combinedResults = results.flatMap((result) => result.hits);
          setSearchResult(combinedResults);
        })
        .catch((error) => {
          toast({
            title: 'Error',
            description: error.message,
            status: 'error',
            duration: 3000,
          });
        });
    },
    [globalState.companies, globalState.currentCompany, toast]
  );

  const handleUserProjectClick = useCallback((userId) => {
    setSelectedUserProjectIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  }, []);

  const handleAddTeamProject = useCallback(async () => {
    if (!access) {
      toast({
        title: t('toast.error'),
        description: 'Please select an access level',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      const collectionName = `flowcharts/${id}/users`;
      const userIds = selectedUserProjectIds.map((x) => x.id);

      await Promise.all(
        selectedUserProjectIds.map((user) =>
          setDocumentFirebase(collectionName, user.id, user)
        )
      );

      await arrayUnionFirebase('flowcharts', id, 'users', userIds);

      if (access === 'editor') {
        await arrayUnionFirebase('flowcharts', id, 'owner', userIds);
      }

      toast({
        title: 'Success',
        description: 'Users added successfully',
        status: 'success',
        duration: 2000,
      });

      setModalProjectUser(false);
      setSelectedUserProjectIds([]);
      setSearchResult([]);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  }, [access, id, selectedUserProjectIds, toast, t]);

  // ==================== UI COMPONENTS ====================
  
  const ColorPalette = () => (
    <Stack gap={2}>
      <Text pt={5}>{t('colorPalette')}</Text>
      <Divider />
      <Grid templateColumns="repeat(5, 1fr)" gap={2}>
        {dataColor?.map((color, i) => (
          <Box
            key={i}
            w={7}
            h={7}
            bgColor={color}
            onClick={() => handleColorChange(color)}
            cursor="pointer"
          />
        ))}
      </Grid>
    </Stack>
  );

  // ==================== RENDER ====================
  
  return (
    <Stack>
      <HStack w="full" h="100vh" align="left" border="1px solid lightgrey">
        {/* Canvas */}
        <Box
          bgColor="gray.200"
          w={owner ? '80%' : '100%'}
          h="100vh"
          borderRadius="md"
        >
          <ReactFlow
            proOptions={PRO_OPTIONS}
            nodesDraggable={owner}
            nodes={nodes}
            edges={edges}
            defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeDrag={onNodeDrag}
            onNodeClick={onNodeClick}
            nodeTypes={NODE_TYPES}
            onNodeDragStart={onNodeDragStart}
            onSelectionDragStart={onSelectionDragStart}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgesDelete}
            connectionLineType={ConnectionLineType.SmoothStep}
            connectionMode={ConnectionMode.Loose}
            fitView
          >
            {/* Title Panel */}
            <Panel position="top-left">
              <HStack
                bgColor="white"
                p={2}
                px={7}
                borderRadius="3xl"
                boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
              >
                <BackButtons />
                <Center height="30px">
                  <Divider orientation="vertical" />
                </Center>
                <Text color="darkgray">{title}</Text>
              </HStack>
            </Panel>

            {/* Control Panel */}
            <Panel position="top-right">
              <HStack
                bgColor="white"
                p={2}
                px={7}
                borderRadius="3xl"
                boxShadow="rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"
              >
                <Text color="lightgrey" size="sm">
                  {t('panelButton')}
                </Text>
                <Center height="30px">
                  <Divider orientation="vertical" />
                </Center>

                <DownloadButtonComponent />

                {owner && (
                  <>
                    <Button
                      bgColor="transparent"
                      size="sm"
                      isDisabled={!canUndo}
                      onClick={undo}
                    >
                      <FcUndo />
                    </Button>

                    <Button
                      bgColor="transparent"
                      size="sm"
                      isDisabled={!canRedo}
                      onClick={redo}
                    >
                      <FcRedo />
                    </Button>

                    <Button
                      bgColor="transparent"
                      onClick={() => setModalProjectUser(true)}
                      size="sm"
                    >
                      <FcShare />
                    </Button>

                    <Button size="sm" colorScheme="blue" onClick={handleSave}>
                      {t('save')}
                    </Button>
                  </>
                )}
              </HStack>
            </Panel>

            <Controls />
            <MiniMap />
          </ReactFlow>
        </Box>

        {/* Sidebar */}
        {owner && (
          <Stack
            p={5}
            spacing={5}
            boxShadow="rgba(60, 64, 67, 0.3) 0px 1px 2px 0px"
            w="20%"
            h="100vh"
            justify="space-between"
          >
            <Stack gap={2}>
              <Text>{t('basicFlowchartShape')}</Text>
              <Divider />
              <Grid templateColumns="repeat(4, 1fr)" gap={2}>
                {dataButton.map((item) => (
                  <Button
                    key={item.shape}
                    h={10}
                    bgColor="transparent"
                    onClick={() => handleAdd(item.shape)}
                  >
                    <img src={item.image} alt={item.type} />
                  </Button>
                ))}
              </Grid>
              <Divider />
              <ColorPalette />
            </Stack>
          </Stack>
        )}

        {/* Share Modal */}
        <ModalAddUser
          title="Flowchart"
          modalProjectUser={modalProjectUser}
          setModalProjectUser={setModalProjectUser}
          handleSearchUsers={handleSearchUsers}
          searchResult={searchResult}
          handleUserProjectClick={handleUserProjectClick}
          owner={owner}
          access={access}
          setAccess={setAccess}
          selectedUserProjectIds={selectedUserProjectIds}
          handleAddTeamProject={handleAddTeamProject}
        />
      </HStack>
    </Stack>
  );
}

// ==================== WRAPPER ====================
const ProductivityFlowchartCanvasPage = () => {
  return (
    <ReactFlowProvider>
      <FlowchartCanvas />
    </ReactFlowProvider>
  );
};

export default ProductivityFlowchartCanvasPage;